/**
 * Allocation chart with dark theme styling.
 */
import { useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { clsx } from 'clsx';
import { formatCurrency, formatPercent } from '@/utils/formatters';

export interface AllocationDataPoint {
  symbol: string;
  value: number;
  percentage: number;
}

interface AllocationChartProps {
  data: AllocationDataPoint[];
  isLoading?: boolean;
  className?: string;
}

// Vibrant color palette for dark theme
const COLORS = [
  '#0ea5e9', // sky
  '#10b981', // emerald
  '#f59e0b', // amber
  '#f43f5e', // rose
  '#a855f7', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#6366f1', // indigo
];

type ViewMode = 'percentage' | 'value';

export function AllocationChart({
  data,
  isLoading = false,
  className,
}: AllocationChartProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('percentage');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className={clsx('space-y-4', className)}>
        <div className="h-6 w-32 rounded shimmer" />
        <div className="mx-auto h-64 w-64 rounded-full shimmer" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={clsx('flex h-64 items-center justify-center', className)}>
        <div className="rounded-lg border border-dashed border-dark-700 bg-dark-800/50 p-8">
          <p className="text-sm text-dark-500">No allocation data available</p>
        </div>
      </div>
    );
  }

  const totalValue = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className={clsx('relative', className)}>
      {/* Header with view toggle */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-medium text-dark-400">Position Allocation</h3>
        <div className="flex gap-1 rounded-lg bg-dark-800 p-1" role="group" aria-label="View mode">
          <button
            onClick={() => setViewMode('percentage')}
            className={clsx(
              'rounded-md px-3 py-1 text-xs font-medium transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              viewMode === 'percentage'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-dark-400 hover:text-dark-200'
            )}
            aria-pressed={viewMode === 'percentage'}
          >
            %
          </button>
          <button
            onClick={() => setViewMode('value')}
            className={clsx(
              'rounded-md px-3 py-1 text-xs font-medium transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              viewMode === 'value'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-dark-400 hover:text-dark-200'
            )}
            aria-pressed={viewMode === 'value'}
          >
            $
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="h-52" role="img" aria-label="Position allocation chart">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={75}
              paddingAngle={2}
              dataKey="value"
              nameKey="symbol"
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {data.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  opacity={activeIndex === null || activeIndex === index ? 1 : 0.5}
                  style={{ transition: 'opacity 0.2s' }}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, _name: string, props) => {
                const item = props.payload as AllocationDataPoint;
                return viewMode === 'percentage'
                  ? formatPercent(item.percentage / 100)
                  : formatCurrency(value);
              }}
              contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.3)',
              }}
              labelStyle={{ color: '#f1f5f9' }}
              itemStyle={{ color: '#94a3b8' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Center label */}
      <div className="pointer-events-none absolute left-1/2 top-[calc(50%+12px)] -translate-x-1/2 -translate-y-1/2">
        <div className="text-center">
          <p className="text-xs text-dark-500">Total</p>
          <p className="text-lg font-bold font-mono text-dark-100">
            {formatCurrency(totalValue)}
          </p>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap justify-center gap-3">
        {data.slice(0, 5).map((item, index) => (
          <div key={item.symbol} className="flex items-center gap-1.5">
            <div
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: COLORS[index % COLORS.length] }}
            />
            <span className="text-xs text-dark-400">
              {item.symbol}{' '}
              <span className="text-dark-500">
                {viewMode === 'percentage'
                  ? formatPercent(item.percentage / 100)
                  : formatCurrency(item.value)}
              </span>
            </span>
          </div>
        ))}
        {data.length > 5 && (
          <span className="text-xs text-dark-500">+{data.length - 5} more</span>
        )}
      </div>

      {/* Accessible data table */}
      <table className="sr-only">
        <caption>Position allocation breakdown</caption>
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Value</th>
            <th>Percentage</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <tr key={item.symbol}>
              <td>{item.symbol}</td>
              <td>{formatCurrency(item.value)}</td>
              <td>{formatPercent(item.percentage / 100)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
